// ============================================================
// CaseLink.tsx
// A link to a case that knows whether the current user may open it.
//   • allowed / unknown → a normal clickable link (onOpen).
//   • locked            → greyed-out text with a lock icon, NOT clickable, with
//                         a "You don't have access to this case" tooltip — no
//                         navigate-then-error dead end.
// Access comes from useCaseAccess (batched, cached POST /api/cases/access-check,
// backed by the server's lib/caseAccess.ts rule).
// ============================================================
import React from 'react'
import { useCaseAccess } from '../../hooks/useCaseAccess'

export const LOCKED_CASE_TIP = "You don't have access to this case"

export const LockIcon: React.FC<{ className?: string }> = ({ className = 'w-3.5 h-3.5' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${className} shrink-0`} aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
)

interface CaseLinkProps {
  caseId: string
  onOpen: (caseId: string) => void
  children: React.ReactNode
  /** Classes for the normal (clickable) link. */
  className?: string
  /** Extra classes for the locked state (layout/size); colour is handled here. */
  lockedClassName?: string
}

const CaseLink: React.FC<CaseLinkProps> = ({ caseId, onOpen, children, className = '', lockedClassName = '' }) => {
  const accessOf = useCaseAccess([caseId])
  const state = accessOf(caseId)
  // Not checked yet → NEUTRAL: plain, non-clickable text (no lock, no link) so
  // it can never flash clickable and then lock. Settles within one batched
  // request (or instantly when primed, e.g. from the appointment payload).
  if (state === 'unknown') {
    return (
      <span aria-busy="true" className={`inline-flex items-center gap-1.5 text-casevo-text-soft cursor-default ${lockedClassName}`}>
        <span>{children}</span>
      </span>
    )
  }
  if (state === 'locked') {
    return (
      <span
        role="link"
        aria-disabled="true"
        title={LOCKED_CASE_TIP}
        className={`inline-flex items-center gap-1.5 text-casevo-text-soft opacity-70 cursor-not-allowed select-none ${lockedClassName}`}
      >
        <LockIcon />
        <span>{children}</span>
        <span className="sr-only">({LOCKED_CASE_TIP})</span>
      </span>
    )
  }
  return (
    <button type="button" onClick={() => onOpen(caseId)} className={className}>
      {children}
    </button>
  )
}

export default CaseLink
